import { useState } from 'react';
import { Avatar } from './Avatar.tsx';
import { G } from './icons.tsx';
import { Ticks } from './Ticks.tsx';
import type { ChatConversation } from './types.js';
import { useT } from '../i18n/I18n.tsx';

/**
 * One row in the conversation list.
 *
 * Density matters more than decoration here: avatar, name, one-line preview,
 * timestamp and any unread count, in a fixed-height row that can be scanned at
 * speed. It is deliberately not a card.
 */

function shortTime(iso: string | null, yesterdayLabel: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  const yesterday = new Date(now.getTime() - 86400000);
  if (date.toDateString() === yesterday.toDateString()) return yesterdayLabel;
  if (now.getTime() - date.getTime() < 7 * 86400000) {
    return date.toLocaleDateString([], { weekday: 'short' });
  }
  return date.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Whole days until an unclaimed invitation stops working. */
function daysUntil(iso: string | undefined): number | null {
  if (!iso) return null;
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  return Number.isFinite(days) ? Math.max(0, days) : null;
}

/**
 * An invitation nobody has joined yet.
 *
 * Deliberately not a conversation row. There is no correspondent, no message
 * and no activity, so an avatar and a preview line would be furniture standing
 * in for content that does not exist. What the owner actually wants here is the
 * link and how long it lasts.
 */
function InvitationRow({
  conversation,
  onSelect,
}: {
  conversation: ChatConversation;
  onSelect: () => void;
}) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const days = daysUntil(conversation.inviteExpiresAt);

  const copy = async () => {
    if (!conversation.invitationUrl) return;
    try {
      await navigator.clipboard.writeText(conversation.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the owner can still open the dialog.
    }
  };

  return (
    <div className="invite-row">
      <button type="button" className="invite-row__main" onClick={onSelect}>
        <span className="invite-row__glyph" aria-hidden="true">
          <G n="link" size={16} />
        </span>
        <span className="invite-row__text">
          <span className="invite-row__title">{t('row.invitationTitle')}</span>
          <span className="invite-row__meta">
            {days === null
              ? t('row.invitationWaiting')
              : days === 0
                ? t('row.invitationExpiresToday')
                : t('row.invitationExpiresIn', { days })}
          </span>
        </span>
      </button>
      {conversation.invitationUrl ? (
        <button
          type="button"
          className="invite-row__copy"
          onClick={() => void copy()}
          aria-live="polite"
        >
          <G n={copied ? 'check' : 'copy'} size={14} />
          {copied ? t('common.copied') : t('common.copyShort')}
        </button>
      ) : null}
    </div>
  );
}

export function ConversationRow({
  conversation,
  selected,
  onSelect,
}: {
  conversation: ChatConversation;
  selected: boolean;
  onSelect: () => void;
}) {
  const t = useT();
  if (conversation.status === 'pending') {
    return <InvitationRow conversation={conversation} onSelect={onSelect} />;
  }
  const classes = [
    'conv-row',
    selected ? 'is-selected' : '',
    conversation.unread > 0 ? 'has-unread' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type="button" className={classes} onClick={onSelect} aria-current={selected}>
      <Avatar name={conversation.hasKey ? conversation.name : null} />
      <span className="conv-row__body">
        <span className="conv-row__top">
          <span className="conv-row__name">{conversation.name}</span>
          <span className="conv-row__time">
            {shortTime(conversation.timestamp, t('row.yesterday'))}
          </span>
        </span>
        <span className="conv-row__bottom">
          <span className="conv-row__preview">
            {conversation.lastOutgoing ? <Ticks state={conversation.lastOutgoing} /> : null}
            {conversation.preview}
          </span>
          <span className="conv-row__badges">
            {/* Pending invitations never reach here, so no key is expected. */}
            {!conversation.hasKey ? (
              <span title={t('row.noKey')} style={{ display: 'inline-flex' }}>
                <G n="lock" size={15} label={t('row.noKey')} />
              </span>
            ) : null}
            {conversation.unread > 0 ? (
              <span className="unread-badge">{conversation.unread}</span>
            ) : null}
          </span>
        </span>
      </span>
    </button>
  );
}
